/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailCollabHomeChips.js
 * @description
 * List-side navigation of shared-mailbox collaboration for `MailHomeScreen` (the mobile
 * counterpart of the admin `MailCollabSidebar`): a collapsible "Teams & tags" card with
 *
 *  - one block per SHARED mailbox: smart views (Mine + unassigned [default] / Assigned to me /
 *    Unassigned / All open / Done), an "Assigned to ..." teammate picker (online first) and its team
 *    tags with open-thread counts,
 *  - the caller's PERSONAL tags (mailbox labels) with unread counts,
 *  - a "Snoozed" smart view with the pending-snooze count.
 *
 * Selecting a pill reports a view object to the parent:
 * `{address, view}` | `{address, assignee, assigneeName}` | `{address, tag}` | `{personalTag}` |
 * `{snoozed: true}` | `null` (cleared). A view flagged `implicit` is the mailbox default, not a pick.
 * Also exports {@link SnoozedRow}, the row used by the Snoozed view (see / cancel snoozes).
 * Renders nothing when the caller has no shared mailbox, tag or snooze.
 */

import React, {useMemo, useState} from 'react';
import {ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../../../components/management/SafeIcon';
import {FolderPill} from '../../../components/mail/MailUiKit';
import {useManagementTheme} from '../../../helpers/managementTheme';
import {COLLAB_VIEWS, userId} from '../../../helpers/mailCollabUtils';
import {PersonFilterSheet} from './MailCollabSheets';

const VIEW_META = {
    mine_unassigned: {icon: 'account-multiple-check-outline', key: 'mail_collab_view_mine_unassigned'},
    mine: {icon: 'account-check-outline', key: 'mail_collab_view_mine'},
    unassigned: {icon: 'account-off-outline', key: 'mail_collab_view_unassigned'},
    open: {icon: 'inbox-multiple-outline', key: 'mail_collab_view_open'},
    done: {icon: 'check-circle-outline', key: 'mail_collab_view_done'},
};

/**
 * Whether `active` equals the candidate view.
 *
 * @param {Object|null} active Active view.
 * @param {Object} candidate Candidate view.
 * @returns {boolean} True when equal.
 */
const sameView = (active, candidate) => Boolean(active)
    && (active.address || '') === (candidate.address || '')
    && (active.view || '') === (candidate.view || '')
    && (active.assignee || '') === (candidate.assignee || '')
    && (active.tag || '') === (candidate.tag || '')
    && (active.personalTag || '') === (candidate.personalTag || '')
    && Boolean(active.snoozed) === Boolean(candidate.snoozed);

/**
 * Collapsible collaboration navigation card.
 *
 * @param {Object} props Props.
 * @param {Object} props.collab `useMailCollab()` result.
 * @param {Object|null} props.view Active collaboration view.
 * @param {(view: Object|null) => void} props.onChange Selection handler (same view again clears).
 * @returns {JSX.Element|null} Card.
 */
export const MailCollabHomeChips = ({collab, view, onChange}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const [open, setOpen] = useState(true);
    // Mailbox address whose "Assigned to ..." picker is open ('' = closed).
    const [personFor, setPersonFor] = useState('');
    const shared = collab.sharedMailboxes;
    const personal = collab.tags.personal || [];
    const snoozeCount = collab.snoozes.length;
    const teamCounts = useMemo(() => {
        const map = {};

        (collab.tags.team || []).forEach((entry) => {
            map[String(entry.address || '').toLowerCase()] = Object.fromEntries((entry.tags || []).map((tag) => [tag.id, tag.open || 0]));
        });

        return map;
    }, [collab.tags.team]);

    if (!shared.length && !personal.length && !snoozeCount) {
        return null;
    }

    const toggle = (candidate) => onChange(sameView(view, candidate) ? null : candidate);

    return (
        <View style={[styles.card, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            <TouchableOpacity onPress={() => setOpen((value) => !value)} activeOpacity={0.8} style={styles.head} accessibilityRole="button">
                <Icon name="account-group-outline" size={17} color={m.colors.primary} />
                <Text style={[styles.headTitle, {color: m.colors.heading}]}>{t('mail_collab_section')}</Text>
                {!!view && !view.implicit && (
                    <TouchableOpacity onPress={() => onChange(null)} hitSlop={8}>
                        <Text style={[styles.clear, {color: m.colors.primary}]}>{t('mail_collab_clear')}</Text>
                    </TouchableOpacity>
                )}
                <Icon name={open ? 'chevron-up' : 'chevron-down'} size={19} color={m.colors.muted} />
            </TouchableOpacity>

            {open && (
                <View style={styles.body}>
                    {shared.map((mailbox) => {
                        const address = String(mailbox.address || '').toLowerCase();
                        const counts = collab.viewCounts[address] || {};

                        return (
                            <View key={address} style={styles.block}>
                                <Text style={[styles.blockTitle, {color: m.colors.subText}]} numberOfLines={1}>
                                    {mailbox.name || mailbox.address}
                                </Text>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pills}>
                                    {COLLAB_VIEWS.map((key) => {
                                        const candidate = {address, view: key};

                                        return (
                                            <FolderPill
                                                key={key}
                                                label={t(VIEW_META[key].key)}
                                                icon={VIEW_META[key].icon}
                                                count={key === 'mine' ? counts.mine : (key === 'done' ? counts.done : 0)}
                                                active={sameView(view, candidate)}
                                                onPress={() => toggle(candidate)}
                                            />
                                        );
                                    })}
                                    <FolderPill
                                        label={view?.address === address && view?.assignee ? (view.assigneeName || t('mail_collab_view_person')) : t('mail_collab_view_person')}
                                        icon="account-search-outline"
                                        active={view?.address === address && !!view?.assignee}
                                        onPress={() => setPersonFor(address)}
                                    />
                                    {(mailbox.tags || []).map((tag) => {
                                        const candidate = {address, tag: tag.id};

                                        return (
                                            <FolderPill
                                                key={tag.id}
                                                label={tag.name}
                                                color={tag.color}
                                                count={teamCounts[address]?.[tag.id] || 0}
                                                active={sameView(view, candidate)}
                                                onPress={() => toggle(candidate)}
                                            />
                                        );
                                    })}
                                </ScrollView>
                            </View>
                        );
                    })}

                    {(personal.length > 0 || snoozeCount > 0) && (
                        <View style={styles.block}>
                            <Text style={[styles.blockTitle, {color: m.colors.subText}]}>{t('mail_collab_personal_tags')}</Text>
                            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pills}>
                                {personal.map((tag) => {
                                    const candidate = {personalTag: tag.id};

                                    return (
                                        <FolderPill
                                            key={tag.id}
                                            label={tag.name}
                                            color={tag.color}
                                            count={tag.unread || 0}
                                            active={sameView(view, candidate)}
                                            onPress={() => toggle(candidate)}
                                        />
                                    );
                                })}
                                <FolderPill
                                    label={t('mail_collab_view_snoozed')}
                                    icon="alarm-snooze"
                                    count={snoozeCount}
                                    active={sameView(view, {snoozed: true})}
                                    onPress={() => toggle({snoozed: true})}
                                />
                            </ScrollView>
                        </View>
                    )}
                </View>
            )}

            <PersonFilterSheet
                visible={!!personFor}
                onClose={() => setPersonFor('')}
                address={personFor}
                meId={collab.meId}
                selectedId={view?.address === personFor ? (view?.assignee || '') : ''}
                fetchMembers={collab.fetchMembers}
                onPick={(member) => {
                    const id = userId(member);

                    setPersonFor('');
                    // Picking yourself is the "Assigned to me" view (same counts, same query).
                    onChange(id === collab.meId
                        ? {address: personFor, view: 'mine'}
                        : {address: personFor, assignee: id, assigneeName: member.name || ''});
                }}
                onClear={() => {
                    setPersonFor('');
                    onChange(null);
                }}
            />
        </View>
    );
};

/**
 * Row of the "Snoozed" smart view: subject, sender, wake-up time and a Cancel action.
 *
 * @param {{snooze: Object, onOpen: (snooze: Object) => void, onCancel: (snooze: Object) => void}} props Props.
 * @returns {JSX.Element} Row.
 */
export const SnoozedRow = ({snooze, onOpen, onCancel}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();

    return (
        <TouchableOpacity
            onPress={() => onOpen(snooze)}
            activeOpacity={0.8}
            style={[styles.snoozeRow, {borderBottomColor: m.colors.borderSoft}]}
            accessibilityRole="button"
        >
            <Icon name={snooze.onlyIfNoReply ? 'reply-outline' : 'clock-outline'} size={20} color={m.colors.primary} />
            <View style={styles.snoozeMain}>
                <Text style={[styles.snoozeSubject, {color: m.colors.heading}]} numberOfLines={1}>
                    {snooze.onlyIfNoReply ? `${t('mail_followup_chip')} · ` : ''}{snooze.subject || t('mail_no_subject')}
                </Text>
                <Text style={[styles.snoozeSub, {color: m.colors.subText}]} numberOfLines={1}>
                    {`${snooze.fromName ? `${snooze.fromName} · ` : ''}${t('mail_collab_snoozed_until', {when: new Date(snooze.until).toLocaleString()})}`}
                </Text>
            </View>
            <TouchableOpacity onPress={() => onCancel(snooze)} hitSlop={8} accessibilityRole="button" accessibilityLabel={t('mail_collab_snooze_cancel')}>
                <Text style={[styles.cancel, {color: m.colors.danger}]}>{t('mail_collab_snooze_cancel')}</Text>
            </TouchableOpacity>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    card: {marginHorizontal: 16, marginTop: 8, borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden'},
    head: {flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 11},
    headTitle: {flex: 1, fontSize: 14, fontWeight: '800'},
    clear: {fontSize: 12, fontWeight: '800'},
    body: {paddingBottom: 10},
    block: {marginTop: 2},
    blockTitle: {fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.6, paddingHorizontal: 14, marginBottom: 6, marginTop: 4},
    pills: {paddingHorizontal: 14, gap: 8},
    snoozeRow: {flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: StyleSheet.hairlineWidth},
    snoozeMain: {flex: 1},
    snoozeSubject: {fontSize: 15, fontWeight: '700'},
    snoozeSub: {fontSize: 12, marginTop: 2},
    cancel: {fontSize: 12, fontWeight: '800'},
});
